/**
 * The two ends of the host liveness beacon (见 18 连接生命周期): the worker holds
 * a Web Lock for its whole life, and each page waits on that same lock to learn
 * the worker died. Naming and rationale live with the contract, in
 * `hostLivenessLockName`.
 *
 * Both halves are here rather than one per project because they are one
 * mechanism, and this module — like `protocol.ts` — touches neither DOM nor
 * WebWorker globals, so the page project and the worker project can both import
 * it. Shaped like `host-lock.ts` (a narrow `LockManager` slice, options object,
 * the never-resolving callback that holds a lock for its holder's lifetime) so it
 * is testable without a browser.
 */

/**
 * Minimal `navigator.locks` surface these two need.
 *
 * `mode` is spelled out at both call sites rather than left to the default,
 * because which one each half uses is the load-bearing part — see
 * {@link watchHostLiveness} on why the watchers must be `shared`.
 *
 * The callback's return type is wider than `host-lock.ts`'s `Promise<never>`
 * because both uses live here: holding never returns, while watching releases as
 * soon as it is granted — the grant *is* the signal, and keeping the lock
 * afterwards would serve nothing.
 */
export interface LockManagerLike {
  request(
    name: string,
    options: { mode: 'exclusive' | 'shared' },
    callback: () => Promise<unknown> | void,
  ): Promise<unknown>;
}

/** Whether the beacon is actually lit, and why not when it is not. */
export type LivenessHold = { held: true } | { held: false; reason: string };

/**
 * Take the beacon and keep it for this worker's whole life.
 *
 * Resolves when the answer is known either way and **never rejects**: the caller
 * awaits this inside host startup, so a throw here would strand the host before
 * it ever serves a port.
 *
 * A `false` answer is reported rather than absorbed. It means this host cannot be
 * observed for the rest of its life, and the page is told so (by the absence of
 * `hostLockName`) instead of being handed a lock name nobody holds — which it
 * would be granted immediately and read as "the host is already dead".
 */
export function holdHostLiveness(options: {
  locks: LockManagerLike | undefined;
  name: string;
}): Promise<LivenessHold> {
  const { locks, name } = options;
  if (!locks) {
    return Promise.resolve({
      held: false,
      reason: 'Web Locks are unavailable in this runtime',
    });
  }

  return new Promise<LivenessHold>((resolve) => {
    let settled = false;
    const settle = (result: LivenessHold): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(result);
    };

    void locks
      // `exclusive` is what makes the beacon *block* the watchers: a shared
      // holder would let every page's request through immediately, and each
      // would read that grant as this host being dead.
      .request(name, { mode: 'exclusive' }, () => {
        settle({ held: true });
        // Never resolves: the browser releases this when the worker is
        // terminated, which is exactly the moment the pages are waiting for.
        return new Promise<never>(() => undefined);
      })
      .catch((err: unknown) => {
        settle({
          held: false,
          reason: `requesting "${name}" failed: ${err instanceof Error ? err.message : String(err)}`,
        });
      });
  });
}

/**
 * Wait for `name` to be released, i.e. for the host that holds it to die.
 *
 * The request stays queued for as long as the host lives, so `onReleased` fires
 * once and means one thing. It can arrive late — a frozen or background tab runs
 * the callback when it is next allowed to — so the caller must treat it as "the
 * host that owned this name is gone", not as "this is current"; a page that has
 * since reconnected will be watching a different name.
 *
 * `onUnavailable` exists so a rejected request cannot pass for a healthy watch:
 * without it this page would sit forever believing it is being told about a death
 * that nothing is watching for.
 */
export function watchHostLiveness(options: {
  locks: LockManagerLike | undefined;
  name: string;
  onReleased: () => void;
  onUnavailable: (reason: string) => void;
}): void {
  const { locks, name, onReleased, onUnavailable } = options;
  if (!locks) {
    onUnavailable('Web Locks are unavailable in this runtime');
    return;
  }

  void locks
    // `shared`, and this one is not a detail: every tab on this origin watches
    // the same name, and exclusive requests are granted one at a time. The tabs
    // would then form a queue in which each waits for the one ahead of it to run
    // its callback and release — so a backgrounded or frozen tab, whose callback
    // does not run until the browser lets it, would hold up the death signal for
    // the tab the user is actually looking at. Shared requests are all granted
    // together the moment the exclusive holder lets go.
    .request(name, { mode: 'shared' }, () => {
      onReleased();
    })
    .catch((err: unknown) => {
      onUnavailable(
        `watching "${name}" failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    });
}
